import { useEffect, useState } from "react";
import { Bell, BellSlash, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const VAPID_PUB = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export default function NotificationToggle() {
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const [supported] = useState(() => "Notification" in window && "serviceWorker" in navigator && "PushManager" in window);
  const [permission, setPermission] = useState(() => (typeof Notification !== "undefined" ? Notification.permission : "default"));
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    (async () => {
      try {
        const { data } = await api.get("/push/status");
        setSubscribed(Boolean(data.subscribed));
      } catch (_e) { /* ignore */ }
    })();
  }, [supported]);

  if (!supported) return null; // Browser doesn't support push (e.g. older iOS Safari)

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Notifications blocked — enable them in your browser settings");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        if (!VAPID_PUB) {
          toast.error("Push not configured");
          return;
        }
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUB),
        });
      }
      const json = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
      setSubscribed(true);
      toast.success("Push notifications enabled");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Could not enable notifications");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint, keys: sub.toJSON().keys });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Push notifications disabled");
    } catch (err) {
      toast.error("Could not disable");
    } finally { setBusy(false); }
  };

  const blocked = permission === "denied";

  return (
    <div className="card-flat p-5 border border-brand/40" data-testid="notification-toggle">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          {subscribed ? (
            <CheckCircle size={22} weight="duotone" className="text-emerald-400 mt-0.5" />
          ) : (
            <Bell size={22} weight="duotone" className="text-brand-light mt-0.5" />
          )}
          <div>
            <div className="font-display text-lg">
              {subscribed ? "Notifications on" : isClient ? "Get session reminders" : "Get client activity pings"}
            </div>
            <p className="text-zinc-400 text-sm mt-1 max-w-md">
              {blocked
                ? "You blocked notifications. Re-enable them from your browser's site settings, then refresh."
                : subscribed
                ? (isClient
                    ? "We'll ping your home-screen icon 24 hours before each session, alongside email."
                    : "We'll ping you when a client uses the bot to reschedule, cancel, or top up.")
                : (isClient
                    ? "Allow notifications and we'll send a tap-to-confirm ping 24 hours before each session — right to your home screen."
                    : "Allow notifications and we'll ping you the moment a client reschedules, cancels, or tops up via the bot.")}
            </p>
          </div>
        </div>
        {!blocked && (
          subscribed ? (
            <button onClick={disable} disabled={busy} className="btn-ghost" data-testid="push-disable">
              <BellSlash size={14} weight="bold" /> {busy ? "Disabling…" : "Turn off"}
            </button>
          ) : (
            <button onClick={enable} disabled={busy} className="btn-primary" data-testid="push-enable">
              <Bell size={14} weight="bold" /> {busy ? "Enabling…" : "Enable"}
            </button>
          )
        )}
      </div>
    </div>
  );
}
